// ════════════════════════════════════════════════════════════════════
// tableSort.js — REUSABLE sortable + filterable table helper.
//
// Headless by design: it does NOT render the table markup (each portal
// table keeps its own bespoke styling, colours and totals row). It only
// owns the sort/filter STATE + the row transform, plus a small <SortableTh>
// header cell you drop in place of a plain <th>.
//
// Usage:
//   const cols = [
//     { key:"date",  label:"Date",  type:"date",   value:r=>r.dateObj },
//     { key:"gross", label:"Gross", type:"number", value:r=>r.gross },
//     { key:"name",  label:"Name",                  value:r=>r.platform }, // type defaults to "text"
//   ];
//   const tbl = useTableSortFilter(rows, cols);
//   // header:  <SortableTh col={cols[0]} table={tbl} thStyle={...} align="left"/>
//   // body:    tbl.rows.map(...)
//
// `type` decides comparison + the filter widget:
//   "text"   → case-insensitive substring filter, locale string sort
//   "number" → numeric sort; filter is a substring match on the raw value
//   "date"   → Date/timestamp sort; filter is a substring on the ISO/string
// A column with no `value` accessor is display-only (no sort, no filter) —
// e.g. an actions column.
// ════════════════════════════════════════════════════════════════════
import { useState, useMemo } from "react";
import { C } from "./theme";

// Coerce a column value to a comparable primitive for sorting.
function toComparable(v, type) {
  if (v == null) return type === "number" ? -Infinity : "";
  if (type === "number") {
    const n = typeof v === "number" ? v : parseFloat(v);
    return isNaN(n) ? -Infinity : n;
  }
  if (type === "date") {
    if (v instanceof Date) return isNaN(v.getTime()) ? -Infinity : v.getTime();
    const t = new Date(v).getTime();
    return isNaN(t) ? -Infinity : t;
  }
  return String(v).toLowerCase();
}

// Coerce a column value to a string for the per-column filter match.
function toFilterText(v) {
  if (v == null) return "";
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return "";
    // Match both ISO (yyyy-mm-dd) and dd/mm/yyyy so either typed form filters.
    const yyyy = v.getFullYear();
    const mm = String(v.getMonth() + 1).padStart(2, "0");
    const dd = String(v.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${dd}/${mm}/${yyyy}`;
  }
  return String(v);
}

/**
 * Headless sort + filter for a finite, client-side row array.
 * @param {Array} rows         the already-computed rows to display
 * @param {Array} columns      [{ key, label, type, value, sortable, filterable }]
 * @param {Object} [opts]      { initialSortKey, initialSortDir }
 */
export function useTableSortFilter(rows, columns, opts = {}) {
  const [sortKey, setSortKey] = useState(opts.initialSortKey ?? null);
  const [sortDir, setSortDir] = useState(opts.initialSortDir ?? "asc"); // "asc" | "desc"
  const [filters, setFilters] = useState({}); // { [key]: string }

  const colByKey = useMemo(() => {
    const m = {};
    (columns || []).forEach(c => { if (c && c.key != null) m[c.key] = c; });
    return m;
  }, [columns]);

  // Click cycles: (other col) → asc → desc → off. Runs in an event handler, so
  // reading the current sortKey/sortDir directly is safe.
  function toggleSort(key) {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null); setSortDir("asc"); // third click clears the sort
  }

  function setFilter(key, val) {
    setFilters(prev => ({ ...prev, [key]: val }));
  }

  const processed = useMemo(() => {
    let out = Array.isArray(rows) ? rows.slice() : [];

    // Filter — every active per-column filter must match (AND).
    const active = Object.entries(filters).filter(([, v]) => v != null && String(v).trim() !== "");
    if (active.length) {
      out = out.filter(r =>
        active.every(([key, needle]) => {
          const col = colByKey[key];
          if (!col || !col.value) return true;
          const hay = toFilterText(col.value(r)).toLowerCase();
          return hay.includes(String(needle).trim().toLowerCase());
        })
      );
    }

    // Sort — stable: keep original order for equal keys.
    if (sortKey && colByKey[sortKey] && colByKey[sortKey].value) {
      const col = colByKey[sortKey];
      const dir = sortDir === "desc" ? -1 : 1;
      const decorated = out.map((r, i) => [r, i]);
      decorated.sort((a, b) => {
        const av = toComparable(col.value(a[0]), col.type);
        const bv = toComparable(col.value(b[0]), col.type);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return a[1] - b[1]; // stable tiebreak
      });
      out = decorated.map(d => d[0]);
    }

    return out;
  }, [rows, filters, sortKey, sortDir, colByKey]);

  return { rows: processed, sortKey, sortDir, toggleSort, filters, setFilter };
}

// Little ▲/▼ indicator. Greyed when this column isn't the active sort.
function SortArrow({ active, dir, color }) {
  const up = "▲", down = "▼";
  return (
    <span style={{ display:"inline-flex", flexDirection:"column", lineHeight:0.7, fontSize:7, marginInlineStart:5, color: color || C.muted2 }}>
      <span style={{ opacity: active && dir === "asc" ? 1 : 0.35 }}>{up}</span>
      <span style={{ opacity: active && dir === "desc" ? 1 : 0.35 }}>{down}</span>
    </span>
  );
}

/**
 * Drop-in <th> replacement that adds click-to-sort (SORT-ONLY).
 * Inherits the host table's header look via `thStyle`; theme colours only.
 *
 * NOTE: per-column typeable filter inputs were removed by owner request — the
 * header is now click-to-sort only. The `useTableSortFilter` hook still exposes
 * filters/setFilter (unused here), so callers don't need to change. The legacy
 * `inputStyle`/`filter` props are accepted but ignored.
 *
 * @param {Object}  col        a column descriptor from the columns array
 * @param {Object}  table      the object returned by useTableSortFilter
 * @param {Object}  thStyle    the existing <th> style for this table
 * @param {String}  align      "left" | "right" (defaults from col.align)
 * @param {String}  arrowColor arrow tint for dark vs light headers
 */
export function SortableTh({ col, table, thStyle = {}, align, arrowColor, inputStyle, filter, children }) {
  const sortable = col && col.value && col.sortable !== false;
  const isActive = table.sortKey === col?.key;
  const a = align || col?.align || (col?.type === "number" || col?.type === "date" ? "right" : "left");

  return (
    <th style={{ ...thStyle, textAlign: a }}>
      <div
        onClick={sortable ? () => table.toggleSort(col.key) : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: a === "right" ? "flex-end" : "flex-start",
          gap: 2,
          cursor: sortable ? "pointer" : "default",
          userSelect: "none",
          width: a === "right" ? "100%" : "auto",
        }}
        title={sortable ? "Sort by this column" : undefined}
      >
        <span>{children ?? col?.label ?? ""}</span>
        {sortable && <SortArrow active={isActive} dir={table.sortDir} color={arrowColor} />}
      </div>
    </th>
  );
}
